import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { IMAGES } from "@/lib/constants";
import { PageBanner } from "@/components/PageBanner";
import { api, mediaUrl } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const DEFAULT_GALLERY = [
  { title: "Our Campus & Students", category: "School Photos", image: IMAGES.hero },
  { title: "Happy Learners", category: "School Photos", image: IMAGES.students },
  { title: "Annual Cultural Function", category: "Annual Function", image: IMAGES.annual },
  { title: "Sports Day", category: "Sports Day", image: IMAGES.sports },
  { title: "Independence Day Celebration", category: "Independence Day", image: IMAGES.independence },
  { title: "Smart Classroom", category: "School Photos", image: IMAGES.smartClass },
  { title: "Computer Lab", category: "Exhibition", image: IMAGES.computerLab },
  { title: "Science Lab", category: "Exhibition", image: IMAGES.scienceLab },
  { title: "Art & Craft", category: "Exhibition", image: IMAGES.artCraft },
];

const CATS = ["All", "School Photos", "Annual Function", "Sports Day", "Exhibition", "Independence Day"];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.getGallery().then((r) => {
      setItems([...r.data, ...DEFAULT_GALLERY]);
    }).catch(() => setItems(DEFAULT_GALLERY));
  }, []);

  const photos = items.filter((i) => i.type !== "video");
  const videos = items.filter((i) => i.type === "video");
  const filtered = active === "All" ? photos : photos.filter((i) => i.category === active);

  return (
    <div>
      <PageBanner title="Gallery" subtitle="Moments of learning, celebration and achievement at B.R.Smart Academy." image={IMAGES.annual} />

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {CATS.map((c) => (
            <button key={c} onClick={() => setActive(c)} data-testid={`gallery-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${active === c ? "bg-brandred text-white" : "bg-white text-navy border border-slate-200 hover:border-brandred"}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {filtered.map((g, i) => (
            <motion.button key={g.id || i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              onClick={() => setLightbox(g)} data-testid={`gallery-item-${i}`}
              className="mb-5 block w-full break-inside-avoid group relative overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
              <img src={mediaUrl(g.image)} alt={g.title} className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <div className="text-left">
                  <span className="text-xs text-gold font-bold uppercase tracking-wide">{g.category}</span>
                  <p className="text-white font-semibold">{g.title}</p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Videos */}
        <div className="mt-16">
          <h3 className="font-head font-extrabold text-2xl text-navy mb-6">Videos</h3>
          {videos.length > 0 ? (
            <div className="grid sm:grid-cols-3 gap-5">
              {videos.map((v) => (
                <a key={v.id} href={v.video_url} target="_blank" rel="noopener noreferrer" data-testid={`gallery-video-${v.id}`}
                  className="relative rounded-2xl overflow-hidden border border-slate-200 aspect-video bg-navy grid place-items-center group">
                  <img src={mediaUrl(v.image)} alt={v.title} className="absolute inset-0 h-full w-full object-cover opacity-60" />
                  <div className="relative text-center text-white px-3">
                    <div className="h-14 w-14 rounded-full bg-brandred grid place-items-center mx-auto mb-2 group-hover:scale-110 transition-transform"><Play className="h-6 w-6 fill-white" /></div>
                    <p className="font-semibold text-sm drop-shadow">{v.title}</p>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No videos yet. The school office can add YouTube videos from the Admin Panel.</p>
          )}
        </div>
      </section>

      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-transparent border-0">
          {lightbox && <img src={mediaUrl(lightbox.image)} alt={lightbox.title} className="w-full rounded-xl" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
